import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  LayoutRectangle,
  NativeSyntheticEvent,
  ScrollView,
  TextInput as RNTextInput,
  TextInputKeyPressEventData
} from 'react-native';
import { Pressable, StyleSheet, useWindowDimensions, View } from 'react-native';
import { KeyboardController } from 'react-native-keyboard-controller';
import { List, Portal, TextInput } from 'react-native-paper';
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withTiming
} from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { scheduleOnRN } from 'react-native-worklets';

import { CheckSmallIcon, DownIcon } from '@/assets';
import { RotatableView } from '@/shared/components/animated/RotatableView';
import { isAndroid, isIOS, isWeb } from '@/shared/utils/platform';
import { webScrollBarStyle } from '@/styles/customStyles';
import { useAppTheme } from '@/styles/hooks/useAppTheme';
import type { AppTheme } from '@/styles/theme';

const ITEM_HEIGHT = 46;
const ANIMATION_DURATION = 200;
const CONTENT_GAP = 4;

const makeStyles = (theme: AppTheme) =>
  StyleSheet.create({
    item: { maxHeight: ITEM_HEIGHT, paddingRight: 16 },
    highlightedItem: { backgroundColor: theme.colors.surfaceContainerHighest },
    shadow: {
      position: 'absolute',
      borderRadius: 4,
      boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)',
      elevation: 8,
      backgroundColor: theme.colors.surfaceContainerLow
    },
    dropdown: {
      borderRadius: 4,
      overflow: 'hidden'
    },
    backdrop: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0
    }
  });

export type DropdownItem<T> = {
  value: T;
  label: string;
  hidden?: boolean;
  disabled?: boolean;
};

type CommonProps<T extends string> = {
  editable?: boolean;
  options: DropdownItem<T>[];
  label: string;
  placeholder: string;
  maxVisibleItems?: number;
  error?: boolean;
  matchInputWidth?: boolean;
};

type SingleProps<T extends string> = {
  multiple?: false;
  selectedOption?: DropdownItem<T>;
  onSelect: (value: T) => void;
};

type MultiProps<T extends string> = {
  multiple: true;
  selectedOptions?: DropdownItem<T>[];
  onSelect: (values: T[]) => void;
};

type Props<T extends string> = CommonProps<T> &
  (SingleProps<T> | MultiProps<T>);

export const DropdownSelect = <T extends string>({
  editable = true,
  options,
  label,
  placeholder,
  maxVisibleItems = 5,
  error,
  matchInputWidth = false,
  ...selection
}: Props<T>) => {
  const theme = useAppTheme();
  const insets = useSafeAreaInsets();
  const styles = useMemo(() => makeStyles(theme), [theme]);

  const inputRef = useRef<RNTextInput>(null);
  const inputContainerRef = useRef<View>(null);

  const selectedValues = useMemo<T[]>(() => {
    if (selection.multiple) {
      return (selection.selectedOptions ?? []).map(({ value }) => value);
    }
    return selection.selectedOption ? [selection.selectedOption.value] : [];
  }, [selection]);

  const inputValue = useMemo(() => {
    if (selection.multiple) {
      return (selection.selectedOptions ?? [])
        .map(({ label: optionLabel }) => optionLabel)
        .join(', ');
    }
    return selection.selectedOption?.label;
  }, [selection]);

  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [inputLayout, setInputLayout] = useState<LayoutRectangle>();
  const [opensUp, setOpensUp] = useState<boolean>(false);
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);

  const scrollRef = useRef<ScrollView>(null);

  const visibleOptions = useMemo(
    () => options.filter(({ hidden }) => !hidden),
    [options]
  );
  const itemCount = Math.min(visibleOptions.length, maxVisibleItems);
  const maxHeight = itemCount * ITEM_HEIGHT;
  const { height: screenHeight } = useWindowDimensions();

  const animatedHeight = useSharedValue<number>(0);
  const animatedOpacity = useSharedValue<number>(0);

  const handleCollapse = useCallback(() => {
    inputRef.current?.blur();
    setHighlightedIndex(-1);

    animatedHeight.set(withTiming(0, { duration: ANIMATION_DURATION }));
    animatedOpacity.set(
      withTiming(0, { duration: ANIMATION_DURATION }, (finished) => {
        if (finished) scheduleOnRN(setIsExpanded, false);
      })
    );
  }, [animatedHeight, animatedOpacity]);

  const handleExpand = useCallback(async () => {
    await KeyboardController.dismiss();

    // Measure before opening
    inputContainerRef.current?.measureInWindow((x, y, width, height) => {
      // Add offset for statusbar on Android
      const adjustedY = isAndroid ? y + insets.top : y;
      setInputLayout({ x, y: adjustedY, width, height });

      // Determine direction
      const spaceBelow = screenHeight - (adjustedY + height);
      const spaceAbove = adjustedY;
      const dropdownHeight = maxHeight + CONTENT_GAP;

      setOpensUp(spaceBelow < dropdownHeight && spaceAbove > spaceBelow);

      setIsExpanded(true);
      animatedHeight.set(
        withTiming(maxHeight, { duration: ANIMATION_DURATION })
      );
      animatedOpacity.set(withTiming(1, { duration: ANIMATION_DURATION }));
    });
  }, [animatedHeight, animatedOpacity, insets.top, maxHeight, screenHeight]);

  const handleToggle = useCallback(() => {
    if (!editable) return;

    if (isExpanded) {
      handleCollapse();
    } else {
      handleExpand();
    }
  }, [editable, isExpanded, handleCollapse, handleExpand]);

  const handleSelect = useCallback(
    (value: T) => {
      if (selection.multiple) {
        const isSelected = selectedValues.includes(value);
        const nextValues = isSelected
          ? selectedValues.filter((current) => current !== value)
          : [...selectedValues, value];
        selection.onSelect(nextValues);
        return;
      }
      handleCollapse();
      selection.onSelect(value);
    },
    [selection, selectedValues, handleCollapse]
  );

  // Keyboard navigation (web only; on native there is no on-screen
  // list traversal via a hardware keyboard).
  const findEnabledIndex = useCallback(
    (start: number, step: 1 | -1): number => {
      let index = start;
      while (index >= 0 && index < visibleOptions.length) {
        if (!visibleOptions[index]?.disabled) return index;
        index += step;
      }
      return -1;
    },
    [visibleOptions]
  );

  // Keep the highlighted item scrolled into view.
  useEffect(() => {
    if (highlightedIndex < 0) return;
    scrollRef.current?.scrollTo({
      y: highlightedIndex * ITEM_HEIGHT,
      animated: true
    });
  }, [highlightedIndex]);

  const handleKeyPress = useCallback(
    (event: NativeSyntheticEvent<TextInputKeyPressEventData>) => {
      if (!isWeb || !editable) return;
      const { key } = event.nativeEvent;

      switch (key) {
        case 'ArrowDown':
        case 'ArrowUp': {
          event.preventDefault?.();
          if (!isExpanded) {
            handleExpand();
            setHighlightedIndex(findEnabledIndex(0, 1));
            return;
          }
          const step = key === 'ArrowDown' ? 1 : -1;
          const next = findEnabledIndex(highlightedIndex + step, step);
          if (next !== -1) setHighlightedIndex(next);
          return;
        }
        case 'Home': {
          event.preventDefault?.();
          if (isExpanded) setHighlightedIndex(findEnabledIndex(0, 1));
          return;
        }
        case 'End': {
          event.preventDefault?.();
          if (isExpanded) {
            setHighlightedIndex(
              findEnabledIndex(visibleOptions.length - 1, -1)
            );
          }
          return;
        }
        case 'Enter':
        case ' ': {
          event.preventDefault?.();
          if (!isExpanded) {
            handleExpand();
            return;
          }
          const option = visibleOptions[highlightedIndex];
          if (option && !option.disabled) handleSelect(option.value);
          return;
        }
        case 'Escape':
        case 'Tab': {
          if (isExpanded) handleCollapse();
          return;
        }
      }
    },
    [
      editable,
      isExpanded,
      highlightedIndex,
      visibleOptions,
      findEnabledIndex,
      handleExpand,
      handleSelect,
      handleCollapse
    ]
  );

  const animatedPositionStyle = useAnimatedStyle(() => {
    if (!inputLayout) return { top: 0 };

    // open at top if space below is not enough
    if (opensUp) {
      return { top: inputLayout.y - animatedHeight.value - CONTENT_GAP };
    }

    return { top: inputLayout.y + inputLayout.height + CONTENT_GAP };
  });

  const animatedContentStyle = useAnimatedStyle(() => ({
    height: animatedHeight.value,
    opacity: animatedOpacity.value
  }));

  const inverted = useMemo(
    () => (opensUp ? { transform: [{ scaleY: -1 }] } : undefined),
    [opensUp]
  );

  const renderItem = useCallback(
    (item: DropdownItem<T>, index: number) => {
      const { value, label: optionLabel, disabled } = item;
      const isSelected = selectedValues.includes(value);
      const isHighlighted = index === highlightedIndex;

      let color: string | undefined;
      if (isSelected) color = theme.colors.primary;
      if (disabled) color = theme.colors.onSurfaceDisabled;

      return (
        <List.Item
          // Todo: Workaround for: https://github.com/callstack/react-native-paper/issues/4810#issuecomment-3445337084
          borderless
          key={value}
          onPress={() => {
            if (!disabled) handleSelect(value);
          }}
          disabled={disabled}
          style={[
            styles.item,
            isHighlighted && styles.highlightedItem,
            inverted
          ]}
          titleStyle={{ color }}
          title={optionLabel}
          right={() => (
            <CheckSmallIcon
              fill={isSelected ? theme.colors.primary : 'transparent'}
              width={22}
              height={22}
            />
          )}
        />
      );
    },
    [
      handleSelect,
      inverted,
      selectedValues,
      highlightedIndex,
      styles.item,
      styles.highlightedItem,
      theme.colors.onSurfaceDisabled,
      theme.colors.primary
    ]
  );

  return (
    <View>
      <View ref={inputContainerRef} collapsable={false}>
        <TextInput
          ref={inputRef}
          label={label}
          placeholder={placeholder}
          accessibilityLabel={label}
          accessibilityHint={placeholder}
          mode="outlined"
          error={error}
          caretHidden
          showSoftInputOnFocus={false}
          onKeyPress={handleKeyPress}
          style={{ backgroundColor: 'transparent' }}
          value={inputValue}
          right={
            <TextInput.Icon
              icon={({ color, size }) => (
                <RotatableView changeRotation={isExpanded}>
                  <DownIcon width={size} height={size} fill={color} />
                </RotatableView>
              )}
              onPress={() => {
                if (editable) handleToggle();
              }}
            />
          }
          onPress={isIOS ? handleExpand : undefined}
          onFocus={isIOS ? undefined : handleExpand}
        />
      </View>
      {isExpanded && inputLayout && (
        <Portal>
          {/* Backdrop: allows to close the dropdown when user taps somewhere on the screen */}
          <Pressable
            accessibilityRole="button"
            style={styles.backdrop}
            onPress={handleCollapse}
          />
          {/* Dropdown */}
          <Animated.View
            style={[
              styles.shadow,
              animatedPositionStyle,
              matchInputWidth && inputLayout
                ? { left: inputLayout.x, width: inputLayout.width }
                : { left: 16, right: 16 }
            ]}
          >
            <Animated.View style={[styles.dropdown, animatedContentStyle]}>
              <Animated.ScrollView
                ref={scrollRef}
                style={[webScrollBarStyle, inverted]}
                nestedScrollEnabled
                bounces={visibleOptions.length > maxVisibleItems}
              >
                {visibleOptions.map((element, index) =>
                  renderItem(element, index)
                )}
              </Animated.ScrollView>
            </Animated.View>
          </Animated.View>
        </Portal>
      )}
    </View>
  );
};
